import {
  CompanyCreationProductType,
  IFixedPricing,
  IPricingResponse,
  IRangedPricing,
  SingleMemberFirmCreationType,
  SingleMemberFirmPricingPackage,
} from '@bupa-kkv/features/company-creation/types';
import { queryKeyStore } from '@bupa-monorepo/shared-frontend/query-key-store';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getPricingPackages } from '../../api/self-employment/get-pricing-packages';

const HUF = Intl.NumberFormat('hu', {
  style: 'decimal',
  maximumFractionDigits: 0,
});

export const PackageIds = {
  [SingleMemberFirmCreationType.SOLE_PROPRIETORSHIP]: {
    [SingleMemberFirmPricingPackage.BASIC]: 'cmc8pvusk000007jj80hz3sv8',
    [SingleMemberFirmPricingPackage.EXTENDED]: 'cmc8ph9og000007jv843yg026',
  },
  [SingleMemberFirmCreationType.SINGLE_MEMBER_LIMITED_LIABILITY_COMPANY]: {
    [SingleMemberFirmPricingPackage.BASIC]: 'cmcn3yj76000107kzg4fmb9dg',
    [SingleMemberFirmPricingPackage.EXTENDED]: 'cmcn3ydae000007kz0rmx0ilr',
  },
};

export const useGetPricingPackages = (params: {
  pricingPackage: SingleMemberFirmPricingPackage;
  type: SingleMemberFirmCreationType;
  couponCode?: string;
  options?: UseQueryOptions<MapPricingResponse, AxiosError>;
}) => {
  const packageId = PackageIds[params.type][params.pricingPackage];

  return useQuery({
    queryKey: queryKeyStore.companyCreation.getPricingPackages(
      packageId,
      params.couponCode
    ).queryKey,
    queryFn: () =>
      getPricingPackages({
        packageId: packageId,
        coupon: params?.couponCode,
      }).then((response) => mapPricingResponse(response, params.type)),
    ...params?.options,
  });
};

const ProductIds = {
  [CompanyCreationProductType.COMPANY_CREATION]: [
    'cmc8pwlkr000007ju05cm38q3',
    'cmc8pzw8g000307jug65pgpp6',
    'cmcn40nnv000207kz393i5cr8',
    'cmcn40xfk000407kz3wll83wn',
  ],
  [CompanyCreationProductType.CONSULTATION]: [
    'cmc8pylki000207jueih90act',
    'cmc8x1v9o0003b4ehu9t2f6qz',
    'cmcn40sir000307kzarab7v8i',
    'cmcnipdas000007l13sm27yj0',
  ],
};

const mapPricingResponse = (
  data: IPricingResponse,
  type: SingleMemberFirmCreationType
) => {
  const isSingleMemberFirmCreationType =
    type ===
    SingleMemberFirmCreationType.SINGLE_MEMBER_LIMITED_LIABILITY_COMPANY;

  const formatRange = (pricing?: IRangedPricing): string | number => {
    if (!pricing) {
      return 0;
    }
    const { minPrice = 0, maxPrice = 0 } = pricing;
    return `${HUF.format(minPrice)} - ${HUF.format(maxPrice)} Ft`;
  };

  const getPrice = (
    pricing?: IFixedPricing | IRangedPricing | null
  ): number | string => {
    if (!pricing) {
      return 0;
    }

    if (isSingleMemberFirmCreationType) {
      return formatRange(pricing as IRangedPricing);
    }

    return (pricing as IFixedPricing).price ?? 0;
  };

  const getFeaturePrice = (productType: string): number | string => {
    const feature = data?.features?.find(({ id }) =>
      ProductIds[productType]?.includes(id)
    );
    return getPrice(feature?.pricing);
  };

  return {
    totalPrice: getPrice(data.totalPricing),
    totalDiscountedPrice: getPrice(data.totalDiscountedPricing),
    createCompanyPrice: getFeaturePrice(
      CompanyCreationProductType.COMPANY_CREATION
    ),
    consultationPrice:
      (
        data?.features?.find(({ id }) =>
          ProductIds[CompanyCreationProductType.CONSULTATION]?.includes(id)
        )?.pricing as IFixedPricing
      )?.price ?? 0,
    discountedConsultationPrice:
      (
        data?.features?.find(({ id }) =>
          ProductIds[CompanyCreationProductType.CONSULTATION]?.includes(id)
        )?.discountedPricing as IFixedPricing
      )?.price ?? 0,
    couponData: data?.features?.find(({ id }) =>
      ProductIds[CompanyCreationProductType.CONSULTATION]?.includes(id)
    )?.redeemedCoupon,
  };
};

export type MapPricingResponse = ReturnType<typeof mapPricingResponse>;
